'use client';

import { useState } from 'react';
import styles from './install-theatre.module.css';

const BARS_ACROSS_THE_WINDOW = 48;

export interface InstallTheatreProps {
  gameName: string;
  backgroundUrl: string | null;
  headline: string;
  shareComplete: number | null;
  facts: readonly string[];
  actions?: React.ReactNode;
  finished?: boolean;
}

export function InstallTheatre({
  gameName,
  backgroundUrl,
  headline,
  shareComplete,
  facts,
  actions,
  finished = false,
}: InstallTheatreProps): React.ReactElement {
  const [urlThatFailedToLoad, setUrlThatFailedToLoad] = useState<string | null>(null);
  const artIsUsable = backgroundUrl !== null && backgroundUrl !== urlThatFailedToLoad;

  const clampedShare = shareComplete === null ? null : Math.min(1, Math.max(0, shareComplete));
  const barsFilled =
    clampedShare === null
      ? BARS_ACROSS_THE_WINDOW
      : Math.round(clampedShare * BARS_ACROSS_THE_WINDOW);

  return (
    <section className={styles.theatre ?? ''} data-finished={finished ? 'true' : 'false'}>
      {artIsUsable ? (
        <img
          className={styles.theatreArt ?? ''}
          src={backgroundUrl}
          alt=""
          onError={() => setUrlThatFailedToLoad(backgroundUrl)}
        />
      ) : null}

      <div className={styles.theatreInner ?? ''}>
        <span className="gb-eyebrow">{headline}</span>
        <h2 className="gb-headline">{gameName}</h2>

        {clampedShare === null ? null : (
          <p className={styles.theatreShare ?? ''}>{Math.round(clampedShare * 100)}%</p>
        )}

        {facts.length > 0 ? (
          <ul className={styles.theatreFacts ?? ''}>
            {facts.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        ) : null}

        <div
          className={styles.theatreStrip ?? ''}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          {...(clampedShare === null ? {} : { 'aria-valuenow': Math.round(clampedShare * 100) })}
          aria-label={`${gameName}: ${headline}`}
        >
          {Array.from({ length: BARS_ACROSS_THE_WINDOW }, (_, index) => (
            <i key={index} data-filled={index < barsFilled ? 'true' : 'false'} />
          ))}
        </div>

        {actions ? <div className={styles.theatreActions ?? ''}>{actions}</div> : null}
      </div>
    </section>
  );
}
